import type { FormEvent } from 'react'
import type { ClientType, RegistrationFormElement } from 'types'
import type { Seller, SellerRegistrationFormData } from 'types/seller'
import type { Buyer, BuyerRegistrationFormData } from 'types/buyer'

import { useState, useEffect } from 'react'
import InputMask from 'react-input-mask'

import { useStore } from 'store'
import { removeSpacesAndDashes, trimFormData } from 'utils'

import useIsClientPage from 'hooks/useIsClientPage'

import ModalContent from 'components/ModalContent'
import Button from 'components/Button'
import InputLabel from 'components/InputLabel'
import Input, { PrefixedInput } from 'components/Input'
import Spinner from 'components/Spinner'
import StyledModal from 'components/layout/StyledModal'
import copy from 'copy.json'

const button = copy.common.button
const text_form = copy.common.form
const buyer_title = copy.buyer.demand_details.modal.edit_client.title
const seller_title = copy.seller.offer_details.modal.edit_client.title

type Props = {
  userType: ClientType
  clientData: Buyer | Seller
  isModalOpen: boolean
  onModalClose: () => void
  refreshData: () => void
}

export default function EditClientModal({
  userType,
  clientData,
  isModalOpen,
  onModalClose,
  refreshData
}: Props) {
  const { api, fieldErrors, clearFieldErrors } = useStore()
  const [loading, setLoading] = useState(false)
  const isClient = useIsClientPage()

  const handleChange = (event: FormEvent<HTMLInputElement>) => {
    clearFieldErrors(event.currentTarget.name)
  }

  const closeModal = () => {
    onModalClose()
    clearFieldErrors()
  }

  const submit = async (event: FormEvent<RegistrationFormElement>) => {
    event.preventDefault()
    setLoading(true)

    const form = event.currentTarget

    const requestData = Array.from(trimFormData(new FormData(form))).reduce(
      (result, [name, value]) => ({ ...result, [name]: value }),
      {} as BuyerRegistrationFormData | SellerRegistrationFormData
    )

    requestData.phone_number = removeSpacesAndDashes(requestData.phone_number)
    requestData.version = clientData.version

    const data = await api.editClient(userType, clientData.id, requestData, isClient)

    if (!data) {
      setLoading(false)
      return
    }

    setLoading(false)
    data === 'IS_CONFLICT' ? form.reset() : closeModal()
    refreshData()
  }

  useEffect(() => {
    setLoading(false)
  }, [])

  return (
    <StyledModal isOpen={isModalOpen} onClose={closeModal}>
      <ModalContent as="form" onSubmit={submit}>
        <h3>{userType === 'buyer' ? buyer_title : seller_title}</h3>

        <InputLabel>
          <span>{text_form.company_name}</span>
          <span>{fieldErrors.company_name}</span>
          <Input
            name="company_name"
            defaultValue={clientData.company_name}
            onChange={handleChange}
          />
        </InputLabel>

        {userType === 'seller' && (
          <InputLabel>
            <span>{text_form.concession_identifier}</span>
            <span>{fieldErrors.concession_identifier}</span>
            <Input
              name="concession_identifier"
              defaultValue={
                'concession_identifier' in clientData ? clientData.concession_identifier : ''
              }
              onChange={handleChange}
              style={{ width: '50%' }}
            />
          </InputLabel>
        )}

        <InputLabel>
          <span>{text_form.phone_number}</span>
          <span>{fieldErrors.phone_number}</span>
          <InputMask
            mask="999 999 999"
            maskPlaceholder={null}
            defaultValue={clientData.phone_number}
            onChange={handleChange}
          >
            <PrefixedInput name="phone_number" prefix="+48" type="tel" style={{ width: '50%' }} />
          </InputMask>
        </InputLabel>

        <Button type="submit">{loading ? <Spinner size={16} /> : button.save}</Button>

        <Button type="button" secondary onClick={closeModal}>
          {button.cancel}
        </Button>
      </ModalContent>
    </StyledModal>
  )
}
